<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="card-body p-4">
        <!-- Single Line Filter Row -->
        <div class="filter-row-single">
          <!-- Vehicle Selection Drop Down -->
          <div class="filter-item">
            <label class="filter-label" for="dispatchNo">Vehicle No:</label>
            <select v-model="selectedVehicleNo" id="dispatchNo" class="form-select filter-control" @change="loadDispatchItems">
              <option disabled value="">Select Vehicle No</option>
              <option v-for="(item, index) in vehicleNumbers" :key="index" :value="item.id">
                {{ item.vehicle_no }}
              </option>
            </select>
          </div>

          <!-- Date Picker -->
          <div class="filter-item">
            <label class="filter-label" for="startDatePicker">Dispatch Date:</label>
            <input
              type="date"
              id="startDatePicker"
              v-model="startDate"
              class="form-control filter-control"
              @change="handleDateChange"
              :max="currentDate"
              :disabled="!selectedVehicleNo"
            />
          </div>

          <!-- Search Button -->
          <div class="filter-item button-item">
            <button @click="performSearch" class="btn button-default btn-search-single" :disabled="loading">
              <span>Search</span>
            </button>
          </div>
        </div>

        <!-- Loading Indicator -->
        <div v-if="loading" class="loading-spinner py-4">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <!-- Show Item List in the Table -->
        <div v-if="showTable && dispatchItems.length > 0" class="modern-table-card mt-4">
          <div class="table-responsive table-scroll-container">
            <table class="table modern-table mb-0">
              <thead>
                <tr>
                  <th class="align-middle text-left header-cell col-item-name">Item Name</th>
                  <th class="align-middle text-center header-cell col-qty">Quantity</th>
                  <th class="align-middle text-center header-cell col-counted">Counted Quantity</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in dispatchItems" :key="index" class="modern-row">
                  <td class="item-name-cell">
                    <div class="d-flex align-items-center">
                      <span>{{ item.item_name }}</span>
                    </div>
                  </td>
                  <td class="text-center font-weight-medium">
                    <span class="qty-badge">{{ parseInt(item.qty) }}</span>
                  </td>
                  <td class="text-center">
                    <input
                      type="number"
                      v-model="item.security_counted_qty"
                      class="form-control quantity-input modern-input mx-auto"
                      min="0"
                      placeholder="0"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Modern Empty State Message -->
        <div v-else-if="showTable && dispatchItems.length === 0" class="modern-empty-state mt-4">
          <div class="empty-state-icon-wrapper">
            <i class="fa fa-inbox empty-state-icon"></i>
          </div>
          <h4 class="empty-state-title">No Gate Pass Records Found</h4>
          <p class="empty-state-description">
            There are no gate pass records found for the selected vehicle and date.
          </p>
        </div>

        <!-- Show Error Message -->
        <div v-if="errorMessage" class="error-message mt-3">{{ errorMessage }}</div>

        <!-- Save and Print buttons -->
        <div v-if="dispatchItems.length > 0" class="d-flex justify-content-end mt-4 gap-2">
          <!-- Print Button -->
          <button
            v-show="savedGatePassId !== null"
            type="button"
            class="btn btn-primary px-4 py-2 font-weight-semibold shadow-sm"
            @click="printGatePass"
            :disabled="loading2"
          >
            <span>Print Receipt</span>
          </button>
          
          <!-- Save Button -->
          <button
            type="button"
            class="btn btn-success px-4 py-2 font-weight-semibold shadow-sm"
            @click="issueGatePass"
            :disabled="loading2 || savedGatePassId !== null"
          >
            <span v-if="loading2" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            <span>{{ savedGatePassId ? 'Saved' : 'Save' }}</span>
          </button>
        </div>

        <!-- Print Receipt Modal/Hidden Section -->
        <div v-if="receiptData" ref="printSection" style="display: none;">
          <div class="receipt-container">
            <!-- Logo -->
            <div class="logo-container">
              <img :src="require('@/assets/images/logo/gas_logo.png')" alt="Laugfs Gas Logo" class="receipt-logo">
              </div>
            
            <div class="receipt-header">
              <h2>Gate Pass Out</h2>
            </div>
            
            <div class="receipt-body">
              <div class="info-grid">
                <div class="info-row-single">
                  <span class="label">Distributor Name:</span>
                  <span class="value">{{ getDistributorName() }}</span>
                </div>

                <div class="info-row-double">
                  <div class="info-col">
                    <span class="label">Dispatch Date:</span>
                    <span class="value">{{ receiptData.dispatch_date }}</span>
                  </div>
                  <div class="info-col">
                    <span class="label">Dispatch Time:</span>
                    <span class="value">{{ receiptData.dispatch_time }}</span>
                  </div>
                </div>

                <div class="info-row-single">
                  <span class="label">Vehicle Number:</span>
                  <span class="value">{{ receiptData.vehicle_number }}</span>
                </div>
              </div>

              <table class="items-table">
                <thead>
                  <tr>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Counted Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in receiptData.items" :key="index">
                    <td>{{ item.item_name }}</td>
                    <td>{{ item.quantity }}</td>
                    <td>{{ item.counted_quantity }}</td>
                  </tr>
                </tbody>
              </table>

              <div class="signatures-section">
                <div class="signature-box">
                  <div class="signature-label">Generated By:</div>
                  <div class="signature-name">{{ getGeneratedBy() }}</div>
                  <div class="signature-date-label">Generated Date & Time:</div>
                  <div class="signature-date">{{ receiptData.generated_date_time }}</div>
                </div>

                <div class="signature-box">
                  <div class="signature-label">Verified By:</div>
                  <div class="signature-space"></div>
                  <div class="signature-date-label">Verified Date & Time:</div>
                  <div class="signature-date-space"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  </div>
</div>
</template>

<script>
import { fetchVehicleListByProfile, fetchDispatchItemsSearch, dispatchlistitem, savegetpass,
  fetchGatePassReceipt} from '@/services/dataService';
import {useToast} from "vue-toastification";

export default {
  data() {
    const currentDate = new Date().toISOString().split('T')[0];
    return {
      selectedVehicleNo: '',
      vehicleNumbers: [],
      dispatchItems: [],
      startDate: currentDate,
      currentDate: new Date().toISOString().split('T')[0],
      token: localStorage.getItem('token_set'),
      showTable: false,
      profileId: localStorage.getItem('profile_id'),
      reportingProfileId: localStorage.getItem('reportingL1ProfileId') || '',
      userCategory: localStorage.getItem('user_label') || '',
      loading: false,
      loading2: false,
      errorMessage: '',
      showSuccessMessage: false,
      vehicleError: '',
      savedGatePassId: null,  
      receiptData: null, 
    };
  },

  created() {
    this.loadVehicleNumbers();
  },

  methods: {
    loadVehicleNumbers() {
      const profileId = localStorage.getItem('profile_id');

      if (!this.token) {
        console.error('Token is missing');
        return;
      }

      fetchVehicleListByProfile(profileId, this.token)
          .then(response => {
            if (response.data.status === 'success') {
              this.vehicleNumbers = response.data.data;
            } else {
              console.error('Failed to fetch vehicle numbers');
            }
          })
          .catch(error => {
            console.error('Error fetching vehicle numbers:', error);
          });
    },

    loadDispatchItems() {
      if (!this.selectedVehicleNo) return;

      dispatchlistitem(this.selectedVehicleNo, this.token)
          .then((response) => {
            if (response.data.status === 'success') {
              this.dispatchItems = response.data.data.map((item) => ({
                ...item,
                security_counted_qty: 0,
              }));
            }
          })
          .catch((error) => {
            console.error('Error fetching dispatch items:', error);
          });
    },

    getDistributorName() {
    const userLabel = localStorage.getItem('user_label');
    
    if (userLabel === 'DISTRIBUTOR') {
      return localStorage.getItem('full_name') || 'N/A';
    } else if (userLabel === 'BACK OFFICE') {
      const reportingL1FirstName = localStorage.getItem('reportingL1FirstName') || '';
      const reportingL1LastName = localStorage.getItem('reportingL1LastName') || '';
      return `${reportingL1FirstName} ${reportingL1LastName}`.trim() || 'N/A';
    } else {
      return localStorage.getItem('full_name') || 'N/A';
    }
  },

  getGeneratedBy() {
    return localStorage.getItem('full_name') || 'N/A';
  },

    issueGatePass() {
      const toast = useToast();

      const invalidItems = this.dispatchItems.filter(item => 
        parseInt(item.qty) !== item.security_counted_qty
      );

      if (invalidItems.length > 0) {
        this.showSuccessMessage = false;
        toast.error("Please check the quantity and count")
        return;
      }

      this.errorMessage = '';// Clear error message if quantities match
      this.showSuccessMessage = true;// Show success message if no invalid items
      this.loading2 = true;// Start loading
      
      const activeProfileId = this.userCategory === 'DISTRIBUTOR BACK OFFICE' ? this.reportingProfileId : this.profileId;

      const payload = {
        vehicle_id: this.selectedVehicleNo,
        created_by: activeProfileId,
        security_checked_by: activeProfileId,
        created_at: this.startDate,
        details: this.dispatchItems.map((item) => ({
          item_id: item.item_id,
          item_qty: item.qty,
          security_counted_qty: item.security_counted_qty,
        })),
      };

      console.log('Gate Pass Data:', payload);

      savegetpass(payload, this.token)
        .then((response) => {
          console.log('Full API Response:', response.data);
          
          if (response.data.status === 'success') {
            this.savedGatePassId = response.data.gate_pass_id;
            console.log('Saved Gate Pass ID:', this.savedGatePassId);
            
            toast.success('Gate pass issued successfully!');
            this.fetchReceiptData();
          } else {
            toast.error('Failed to issue gate pass.');
          }
        })
        .catch((error) => {
          console.error('Error issuing gate pass:', error);
          toast.error('Error issuing gate pass: ' + error.message);
        })
        .finally(() => {
          this.loading2 = false;
        });
    },

    // Fetch receipt data
fetchReceiptData() {
  if (!this.savedGatePassId) {
    console.error('❌ No saved gate pass ID');
    return;
  }

  console.log('🚀 Fetching receipt for Gate Pass ID:', this.savedGatePassId);
  console.log('📍 API Endpoint:', `${process.env.VUE_APP_API_BASE_URL || 'YOUR_API_URL'}/web/gatepass/receipt/${this.savedGatePassId}`);
  console.log('🔑 Token:', this.token);

  fetchGatePassReceipt(this.savedGatePassId, this.token)
    .then((response) => {
      console.log('✅ Full API Response:', response);
      console.log('📦 Response Data:', response.data);
      
      if (response.data.status === 'success') {
        this.receiptData = response.data.data;
        console.log('✅ Receipt Data Loaded:', this.receiptData);
        console.log('📋 Items Count:', this.receiptData.items ? this.receiptData.items.length : 0);
      } else {
        console.error('❌ API returned non-success status:', response.data);
        const toast = useToast();
        toast.error('Failed to load receipt data');
      }
    })
    .catch((error) => {
      console.error('❌ Error fetching receipt data:', error);
      console.error('❌ Error Response:', error.response);
      console.error('❌ Error Message:', error.message);
      
      const toast = useToast();
      toast.error('Error fetching receipt data: ' + error.message);
    });
},

    // Print receipt
    printGatePass() {
      if (!this.receiptData) {
        const toast = useToast();
        toast.error('Receipt data not available. Please wait...');
        
        // Try to fetch receipt data
        this.fetchReceiptData();
        
        // Wait and try again
        setTimeout(() => {
          if (this.receiptData) {
            this.executePrint();
          } else {
            toast.error('Failed to load receipt data');
          }
        }, 1000);
      } else {
        this.executePrint();
      }
    },

    executePrint() {
  if (!this.receiptData) {
    const toast = useToast();
    toast.error('Receipt data not loaded');
    return;
  }

  this.$nextTick(() => {
    const printSection = this.$refs.printSection;
    
    if (!printSection) {
      console.error('Print section ref not found');
      const toast = useToast();
      toast.error('Print section not ready');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    
    if (!printWindow) {
      const toast = useToast();
      toast.error('Please allow pop-ups to print');
      return;
    }
    
    // Get the logo as base64
    const logoImg = new Image();
    logoImg.src = require('@/assets/images/logo/gas_logo.png');
    
    logoImg.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = logoImg.width;
      canvas.height = logoImg.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(logoImg, 0, 0);
      const logoBase64 = canvas.toDataURL('image/png');
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Gate Pass Receipt</title>
          <style>
            /* ── Reset ── */
            *, *::before, *::after {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            @page {
              size: A5 portrait;
              margin: 7mm;
            }

            body {
              font-family: 'Arial', 'Helvetica', sans-serif;
              font-size: 8.5pt;
              line-height: 1.35;
              color: #1a1a1a;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .receipt-wrapper { width: 100%; }

            /* ── HEADER: logo left | info right ── */
            .receipt-header {
              display: table;
              width: 100%;
              margin-bottom: 6px;
            }
            .header-logo-cell {
              display: table-cell;
              vertical-align: middle;
              width: 38%;
            }
            .header-logo-cell img {
              max-width: 110px;
              max-height: 52px;
              object-fit: contain;
            }
            .header-info-cell {
              display: table-cell;
              vertical-align: middle;
              width: 62%;
              text-align: right;
            }
            .doc-title {
              font-size: 13pt;
              font-weight: 700;
              color: #1a1a1a;
              letter-spacing: 0.4px;
              margin-bottom: 5px;
            }
            .meta-table {
              display: inline-table;
              text-align: left;
              border-collapse: collapse;
            }
            .meta-table td {
              padding: 1.5px 3px;
              font-size: 7.8pt;
              vertical-align: top;
            }
            .meta-table td.meta-label {
              font-weight: 700;
              color: #1a1a1a;
              white-space: nowrap;
              padding-right: 6px;
            }
            .meta-table td.meta-value { color: #444; }

            /* ── Divider ── */
            .divider {
              border: none;
              border-top: 0.5pt solid #b0b0b0;
              margin: 7px 0;
            }

            /* ── Items Table ── */
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              page-break-inside: auto;
            }
            .items-table thead { display: table-header-group; }
            .items-table tr { page-break-inside: avoid; }
            .items-table th {
              background-color: #2c2c2c;
              color: #fff;
              font-weight: 700;
              font-size: 7.8pt;
              padding: 5px 6px;
              text-align: left;
              border: 1px solid #2c2c2c;
            }
            .items-table th.tc { text-align: center; }
            .items-table td {
              font-size: 7.8pt;
              padding: 3.5px 6px;
              border: 1px solid #d8d8d8;
              color: #1a1a1a;
              vertical-align: middle;
            }
            .items-table td.tc { text-align: center; }
            .items-table tbody tr:nth-child(even) td { background-color: #f7f7f7; }

            /* ── Footer: two equal columns, no borders/dividers ── */
            .footer-section {
              display: table;
              width: 100%;
              margin-top: 10px;
              page-break-inside: avoid;
            }
            .footer-col {
              display: table-cell;
              width: 50%;
              vertical-align: top;
              padding-right: 8px;
            }
            .footer-col.right {
              padding-right: 0;
              padding-left: 8px;
            }
            .footer-col-title {
              font-size: 8.5pt;
              font-weight: 700;
              color: #1a1a1a;
              margin-bottom: 9px;
            }
            /* Each row: label row + dotted-value row */
            .footer-row {
              margin-bottom: 9px;
            }
            .footer-row-label {
              font-size: 7pt;
              font-weight: 600;
              color: #777;
              text-transform: uppercase;
              letter-spacing: 0.25px;
              margin-bottom: 1px;
            }
            /* The dotted line — value sits INSIDE it */
            .footer-dot-line {
              width: 100%;
              border-bottom: 1pt dotted #bbb;
              min-height: 16px;
              font-size: 8pt;
              color: #1a1a1a;
              padding-bottom: 2px;
              line-height: 1.2;
            }
            /* empty dotted line (no value) keeps the same height */
            .footer-dot-line.empty {
              min-height: 16px;
            }

            @media print {
              @page { size: A5 portrait; margin: 7mm; }
              body { margin: 0; }
              .items-table { page-break-inside: auto; }
              .items-table tr { page-break-inside: avoid; }
              .footer-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-wrapper">

            <!-- HEADER -->
            <div class="receipt-header">
              <div class="header-logo-cell">
                <img src="${logoBase64}" alt="Laugfs Gas Logo">
              </div>
              <div class="header-info-cell">
                <div class="doc-title">Gate Pass Out</div>
                <table class="meta-table">
                  <tr>
                    <td class="meta-label">Distributor Name</td>
                    <td class="meta-value">${this.getDistributorName()}</td>
                  </tr>
                  <tr>
                    <td class="meta-label">Dispatch Date</td>
                    <td class="meta-value">${this.receiptData.dispatch_date}</td>
                  </tr>
                  <tr>
                    <td class="meta-label">Dispatch Time</td>
                    <td class="meta-value">${this.receiptData.dispatch_time}</td>
                  </tr>
                  <tr>
                    <td class="meta-label">Vehicle Number</td>
                    <td class="meta-value">${this.receiptData.vehicle_number}</td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- DIVIDER -->
            <hr class="divider">

            <!-- ITEMS TABLE -->
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width:56%;">Item Name</th>
                  <th class="tc" style="width:22%;">Quantity</th>
                  <th class="tc" style="width:22%;">Counted Qty</th>
                </tr>
              </thead>
              <tbody>
                ${this.receiptData.items.map(item => `
                  <tr>
                    <td>${item.item_name}</td>
                    <td class="tc">${item.quantity}</td>
                    <td class="tc">${item.counted_quantity}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <!-- FOOTER: Generated By | Verified By -->
            <div class="footer-section">

              <!-- Left: Generated By -->
              <div class="footer-col">
                <div class="footer-col-title">Generated By</div>

                <div class="footer-row">
                  <div class="footer-row-label">Name</div>
                  <div class="footer-dot-line">${this.getGeneratedBy()}</div>
                </div>

                <div class="footer-row">
                  <div class="footer-row-label">Date &amp; Time</div>
                  <div class="footer-dot-line">${this.receiptData.generated_date_time}</div>
                </div>

                <div class="footer-row">
                  <div class="footer-row-label">Signature</div>
                  <div class="footer-dot-line empty"></div>
                </div>
              </div>

              <!-- Right: Verified By -->
              <div class="footer-col right">
                <div class="footer-col-title">Verified By</div>

                <div class="footer-row">
                  <div class="footer-row-label">Name</div>
                  <div class="footer-dot-line empty"></div>
                </div>

                <div class="footer-row">
                  <div class="footer-row-label">Date &amp; Time</div>
                  <div class="footer-dot-line empty"></div>
                </div>

                <div class="footer-row">
                  <div class="footer-row-label">Signature</div>
                  <div class="footer-dot-line empty"></div>
                </div>
              </div>

            </div>

          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      
      printWindow.onload = function() {
        printWindow.focus();
        
        setTimeout(() => {
          printWindow.print();
          
          printWindow.onafterprint = function() {
            printWindow.close();
          };
          
          setTimeout(() => {
            if (!printWindow.closed) {
              printWindow.close();
            }
          }, 1000);
        }, 500);
      };
    };
    
    logoImg.onerror = () => {
      console.error('Logo failed to load');
      const toast = useToast();
      toast.error('Logo failed to load');
    };
  });
},

    resetForm() {
      this.selectedVehicleNo = '';
      this.dispatchItems = [];
      this.showTable = false;
      this.errorMessage = '';
      this.showSuccessMessage = false;
      this.vehicleError = '';
      this.savedGatePassId = null;
      this.receiptData = null;
    },

    cancel() {
      this.resetForm();
    },

    handleDateChange() {
      console.log('Date changed:', this.startDate);
    },

    performSearch() {
      const vehicleId = this.selectedVehicleNo;
      const date = this.startDate;

      if (!vehicleId) {
        const toast = useToast();
        toast.error('Please select a vehicle number.');
        return;
      } else {
        this.vehicleError = '';
      }

      if (!date) {
        const toast = useToast();
        toast.error('Date is missing.'); 
        console.error('Date is missing:', date);
        return;
      }

      this.loading = true;
      const payload = {
        vehicel_id: vehicleId,
        date: date,
      };

      fetchDispatchItemsSearch(payload, this.token)
          .then(response => {
            if (response.data.status === 'success') {
              this.dispatchItems = response.data.data.map((item) => ({
                ...item,
                security_counted_qty: 0,
              }));
              this.showTable = true;
            } else {
              this.dispatchItems = [];
              this.showTable = true;
            }
          })
          .catch(error => {
            console.error('Error performing search:', error);
          })
          .finally(() => {
            this.loading = false;
          });
    },
  },
};
</script>
<style scoped>
/* Single Line Filter Row Layout */
.filter-row-single {
  display: flex;
  align-items: flex-end;
  flex-wrap: nowrap;
  gap: 16px;
  width: 100%;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.filter-item.button-item {
  flex: 0 0 auto;
}

.filter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0;
  white-space: nowrap;
}

.filter-control {
  height: 42px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  font-size: 0.9rem;
  color: #1e293b;
  padding: 0 12px;
  width: 100%;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.filter-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
  outline: none;
}

.btn-search-single {
  height: 42px;
  padding: 0 24px;
  background-color: #007bff;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 6px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-search-single:hover {
  background-color: #0056b3;
  box-shadow: 0 4px 6px -1px rgba(0, 123, 255, 0.2);
}

.btn-search-single:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Modern Empty State */
.modern-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 24px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  text-align: center;
}

.empty-state-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #eff6ff;
  color: #007bff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.empty-state-icon {
  font-size: 1.6rem;
}

.empty-state-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.empty-state-description {
  font-size: 0.85rem;
  color: #64748b;
  max-width: 420px;
  margin: 0;
  line-height: 1.5;
}

/* Loading spinner styles */
.loading-spinner {
  font-size: 16px;
  text-align: center;
  color: #007bff;
  font-weight: 500;
}

.error-message {
  color: #dc2626;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
}

.table-scroll-container {
  max-height: 55vh;
  overflow-y: auto;
}

/* Modern Table Container Card */
.modern-table-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.modern-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}

/* Header Styling */
.modern-table thead {
  background-color: #1e293b;
  position: sticky;
  top: 0;
  z-index: 10;
}

.modern-table .header-cell {
  background-color: #1e293b !important;
  color: #f8fafc !important;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 12px 14px;
  border-bottom: 1px solid #334155 !important;
  border-top: none !important;
  white-space: nowrap;
}

.col-item-name {
  min-width: 220px;
}

.col-qty {
  width: 100px;
}

.col-counted {
  width: 130px;
}

/* Row & Cell Styling */
.modern-row {
  transition: background-color 0.15s ease;
}

.modern-row td {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 0.88rem;
  color: #334155;
}

.modern-row:hover {
  background-color: #f8fafc;
}

.modern-row:last-child td {
  border-bottom: none;
}

/* Component Badges */
.item-name-cell {
  font-weight: 600;
  color: #0f172a;
}

.qty-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #f1f5f9;
  color: #334155;
  font-weight: 600;
  font-size: 0.85rem;
}

/* Modern Inputs */
.quantity-input.modern-input {
  width: 90px !important;
  height: 34px;
  padding: 4px 6px;
  text-align: center;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.quantity-input.modern-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  outline: none;
  background-color: #fff;
}

.gap-2 {
  gap: 10px;
}

/* Receipt Print section styles */
.receipt-container {
  background: white;
  max-width: 800px;
  margin: 0 auto;
}

.logo-container {
  text-align: center;
  margin-bottom: 20px;
}

.receipt-logo {
  max-width: 180px;
  height: auto;
  display: block;
  margin: 0 auto;
}

.receipt-header {
  text-align: center;
  border-bottom: 3px solid #000;
  padding-bottom: 15px;
  margin-bottom: 25px;
}

.receipt-header h2 {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
}

.info-grid {
  margin-bottom: 25px;
}

.info-row-single,
.info-row-double {
  margin-bottom: 12px;
  padding: 8px 0;
}

.info-row-double {
  display: flex;
  justify-content: space-between;
  gap: 30px;
}

.info-col {
  flex: 1;
}

.label {
  font-weight: bold;
  margin-right: 10px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
}

.items-table th,
.items-table td {
  border: 1px solid #333;
  padding: 10px 12px;
  text-align: left;
}

.items-table th {
  background-color: #000;
  color: #fff;
  font-weight: bold;
}

.signatures-section {
  display: flex;
  justify-content: space-between;
  gap: 40px;
  margin-top: 40px;
}

.signature-box {
  flex: 1;
}

.signature-label {
  font-weight: bold;
  margin-bottom: 8px;
}

.signature-name {
  margin-bottom: 35px;
}

.signature-space {
  height: 40px;
  border-bottom: 1px solid #999;
  margin-bottom: 5px;
}

.signature-date-label {
  font-weight: bold;
  margin-top: 15px;
  margin-bottom: 5px;
}

.signature-date-space {
  border-bottom: 1px solid #999;
  height: 25px;
}

@media (max-width: 768px) {
  .filter-row-single {
    flex-wrap: wrap;
  }
  .filter-item {
    flex: 1 1 100%;
  }
  .filter-item.button-item {
    flex: 1 1 100%;
  }
  .btn-search-single {
    width: 100%;
  }
}
</style>